import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, message,Typography } from "antd";
import React from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { NextIcon } from "../../../../../../assets/svg";
import { StepCard, type StepCardAction } from "../../../../../molecules/StepCard";
import { AddPlanAccordion } from "../../../../BookingForm/components/Commercial/add-plan";
import { BreakdownStaleProvider } from "../../../../BookingForm/components/Commercial/add-plan/BreakdownStaleContext";
import type { CommercialRenewalStepProps } from "../types";
import type { PaymentPlansStepFormValues } from "./types";
import { usePaymentPlansStepController } from "./usePaymentPlansStepController";

const { Text } = Typography;

export const PaymentPlansStep = (props: CommercialRenewalStepProps) => {
  const form = useForm<PaymentPlansStepFormValues>({
    defaultValues: { plans: [] },
  });

  return (
    <FormProvider {...form}>
      <PaymentPlansStepContent {...props} />
    </FormProvider>
  );
};

const PaymentPlansStepContent = (props: CommercialRenewalStepProps) => {
  const { title, onDiscard, onNext, onBack, paymentPlansInvalidated, onPaymentPlansSaved, isSentForApproval } = props;

  const { trigger, getValues } = useFormContext<PaymentPlansStepFormValues>();

  const {
    plansFields,
    selectedBreakdownMode,
    paymentPlanResetKey,
    canAddPlan,
    yearLimit,
    handleAddPlan,
    handleRemovePlan,
    registerPaymentPlanBreakdownStale,
    registerServiceChargeBreakdownStale,
    assertNoStaleCommercialBreakdowns,
    savePaymentPlans,
    isPendingSavePaymentPlans,
  } = usePaymentPlansStepController({
    paymentPlansInvalidated,
    onPaymentPlansSaved,
  });

  const saveValidatedPlans = async () => {
    if (plansFields.length !== yearLimit) {
      message.info(
        yearLimit === 1
          ? "Please add a payment plan for the renewal year before proceeding"
          : `Please add payment plans for all ${yearLimit} renewal years before proceeding`,
      );
      return false;
    }

    const plans = getValues("plans") ?? [];
    const missingBreakdownIndex = plans.findIndex((plan) => !plan?.billingScheduleRows?.length);
    if (missingBreakdownIndex !== -1) {
      const yearLabel = `Year ${plans[missingBreakdownIndex]?.noOfYear ?? missingBreakdownIndex + 1}`;
      message.error(`${yearLabel}: Please generate the billing schedule breakdown before proceeding`);
      return false;
    }

    if (!assertNoStaleCommercialBreakdowns()) return false;

    const plansValid = await trigger("plans", { shouldFocus: true });
    if (!plansValid) return false;

    return savePaymentPlans();
  };

  const handleSaveDraft = async () => {
    await saveValidatedPlans();
  };

  const handleNext = async () => {
    if (isSentForApproval) {
      onNext?.();
      return;
    }

    const ok = await saveValidatedPlans();
    if (!ok) return;
    onNext?.();
  };

  const actions: StepCardAction[] = [
    {
      key: "discard",
      label: "Discard",
      variant: "secondary",
      onClick: onDiscard,
      disabled: isPendingSavePaymentPlans,
    },
    ...(isSentForApproval
      ? []
      : [
          {
            key: "draft",
            label: "Save Draft",
            variant: "subtle" as const,
            onClick: handleSaveDraft,
            disabled: isPendingSavePaymentPlans,
          },
        ]),
    {
      key: "next",
      label: "Next",
      variant: "primary",
      icon: <NextIcon width="16px" height="16px" />,
      iconPosition: "end",
      onClick: handleNext,
      disabled: isPendingSavePaymentPlans,
    },
  ];

  return (
    <StepCard title={title} onBack={onBack} actions={actions}>
      <Form layout="vertical" className="bf-main">
        <Col span={24}>
          <BreakdownStaleProvider
            registerPaymentPlanBreakdownStale={registerPaymentPlanBreakdownStale}
            registerServiceChargeBreakdownStale={registerServiceChargeBreakdownStale}
            assertNoStaleCommercialBreakdowns={assertNoStaleCommercialBreakdowns}
          >
            <AddPlanAccordion
              key={paymentPlanResetKey}
              resetKey={paymentPlanResetKey}
              fields={plansFields}
              remove={handleRemovePlan}
              selectedBreakdownMode={selectedBreakdownMode}
            />
          </BreakdownStaleProvider>
        </Col>
        {canAddPlan && (
          <Col span={24}>
            <Button type="dashed" onClick={handleAddPlan} htmlType="button" icon={<PlusOutlined />} block>
              <Text className="ag-primary fontWeight600" style={{ fontSize: "14px" }}>
                Add Payment Plans
              </Text>
            </Button>
          </Col>
        )}
      </Form>
    </StepCard>
  );
};
